import React, {Fragment, ReactElement, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {SearchBox} from "../../components/SearchBox";
import {Route, Routes, useNavigate} from "react-router-dom";

import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";

import {SortColumn} from "../../api";
import {useGetWorkOrderQuery, usePutWorkOrderByIdMutation, WorkOrderApiGet} from "./workorder_api";
import placeholder from "../../../assets/imgs/workorder.jpg";
import Timer from "@mui/icons-material/Timer";
import Upload from "@mui/icons-material/Upload";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";
import usePaginator from "../../hooks/usePaginator";
import {ListCardDataCol, ListGrid} from "../../components/styled";
import {useHasRole} from "../../hooks/useRole";
import {usePostRegistrationMutation} from "../registration/reg_api";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {CurrentUserInfo} from "../../session_state";
import {Tables} from "../../apitypes";


export type WorkOrderItemProps = {
    item: WorkOrderApiGet
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

const WorkOrderItem = (props: WorkOrderItemProps) => {
    const {item, selectable, onSelect} = props;

    const isAdmin = useHasRole(["Admin"])
    const isForeman = useHasRole(["Foreman"])
    const [uploading, setUploading] = useState(false)
    const [sortedProducts, setSortedProducts] = useState<Tables<"workOrderOrderedProduct">[]>([])
    const [put, {error}] = usePutWorkOrderByIdMutation()
    const onUpload = (path: string) => {
        put({body: {image_url: path}, id: item.id})
    }
    const navigate = useNavigate();
    const cu = useSelector<RootState, CurrentUserInfo | null>(s => s.sessionState.user);
    const defWorker = cu?.defaultWorkerID || null;
    const [startRegistration, {error: err}] = usePostRegistrationMutation()
    const imageUrl = useDownloadImage("products", item.image_url)

    useEffect(() => {
        if (item.workOrderOrderedProduct == null) {
            setSortedProducts([])
            return
        }
        let s = [...item.workOrderOrderedProduct]
        setSortedProducts(s.sort((a, b) => a.position - b.position))
    }, [item]);

    const handleStartRegistration = async () => {

        const {data, error} = await startRegistration({
            WorkerID: defWorker,
            Status: "RegNew",
            WorkOrderID: item.id
        });

        console.log("start reg", data, error);
        if (data && data?.id) {
            navigate("/reg/" + data.id + "/")
        }

    }

    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card onClick={() => {
        selectable && onSelect && onSelect(item.id);
    }} sx={{
        display: "flex"

    }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={imageUrl || placeholder}
            alt={item.number + " " + item.buyerName}
        />
        <ListCardDataCol>
            <CardContent>
                {err && <Alert severity={"error"}>{err?.message}</Alert>}
                <Typography variant={"caption"}>{item.status}</Typography>
                <Typography
                    variant={"subtitle2"}
                    component={"p"}
                    sx={{whiteSpace: "nowrap", textOverflow: "ellipsis"}}
                    color="secondary">
                    {item.number}
                </Typography>
                <Typography variant={"h6"} sx={{
                    whiteSpace: "wrap",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word"
                }}>{item.buyerName}</Typography>
                <Typography variant={"body1"}>{(new Date(item.date)).toLocaleDateString()}</Typography>
                {item.plannedCompletionDate && <Typography variant={"body1"}
                                                           sx={{color: "secondary.main"}}>Rok: {new Date(item.plannedCompletionDate).toLocaleDateString()}</Typography>}
                {sortedProducts.map(param => <Typography variant={"body1"} key={param.id}>
                    {param.calculationQuantity} x {param.productName} ({param.productCode})

                </Typography>)}
            </CardContent>
            {!selectable && <CardActions>
                {(isAdmin || isForeman) && <Button onClick={() => setUploading(true)}
                                                   title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>}
                <Button onClick={() => handleStartRegistration()}
                        title={"Začni registracijo"}
                ><Timer fontSize={"small"}/></Button>
            </CardActions>}
        </ListCardDataCol>

        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"products"}
            onClose={() => setUploading(false)}/>
    </Card>
}


export type WorkOrderListProps = {
    list: WorkOrderApiGet[],
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

export const WorkOrderList = (props: WorkOrderListProps) => {
    const {list, onSelect, selectable} = props;

    return <ListGrid>
        {list.map(param => <WorkOrderItem key={param.id}
                                          item={param}
                                          onSelect={onSelect}
                                          selectable={selectable}/>
        )}
    </ListGrid>
}

type WorkOrderControllerProps = {
    pageSize?: number;
    selectable?: boolean;
    onSelect?: (id: number) => void;
    customFilter?: ReactElement
}

function buildSort(sort: string, dir: "asc" | "desc"): SortColumn[] {
    if (sort != "") {
        return [{col: sort, dir}]
    }
    return []
}

const sortFields = {
    number: "Številka",
    buyerName: "Kupec",
    date: "Datum",

}

export const WorkOrderListController = function (
    {
        pageSize, selectable, onSelect
    }
    : WorkOrderControllerProps) {
    const {
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("workOrders", sortFields)

    const {data, error, isFetching} = useGetWorkOrderQuery({
        filter,
        active: selectable,
        pageSize: pageSize || 25,
        page: page - 1,
        sort: buildSort(sortField, sortDirection)
    });

    if (error) {
        return <SnackError error={error}/>;
    }

    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            <SearchBox
                value={filter}
                onChange={setFilter}
            />
            <SortBox
                sortDirection={sortDirection}
                sortField={sortField}
                onChangeDirection={setSortDirection}
                onSelectField={setSortField}
                fields={sortFields}
            />
        </Toolbar>
        {error && <SnackError error={error}/>}
        {(isFetching || !data) && <CircularProgress/>}
        {(!isFetching && data?.result) &&
            <WorkOrderList
                list={data.result || []}
                selectable={selectable}
                onSelect={onSelect}
            />}
        <Pagination
            page={page}
            onChange={(e, page) => setPage(page)}
            count={Math.ceil((data?.count || 0) / (pageSize || 25))}
        />
    </Fragment>
}

export type WorkOrderProps = {
    pageSize?: number;
}

export default ({pageSize = 15}: WorkOrderProps) => {

    return <Routes>
        <Route path={"/"} element={<Fragment>
            <Title title={"Delovni nalogi"}/>
            <WorkOrderListController pageSize={pageSize}/>
        </Fragment>}/>

    </Routes>
}
